import {
  DataTypesAPIResponse,
  FIELDS_UI_LABELS,
  getCompositeFormatKey,
} from '../components/fields/FieldsSettings/FieldsSettingsTypes';

/**
 * Function to get the UI labels for the available options of a given data type.
 * This is useful for providing a more user-friendly display of data type options in the UI.
 *
 * For amount/number types with thousandsGrouping, this generates composite options
 * that combine decimalSymbol and thousandsGrouping into single selectable formats.
 *
 * @param {string} [dataType] - The type of the data for which UI labels are needed, e.g., 'date', 'amount', 'number'.
 * @param {DataTypesAPIResponse[]} [dataTypesResponse] - The API response that contains the available data type options.
 *
 * @returns {Object} - Returns an object mapping each data type option to its corresponding UI label.
 */
export const getDataUIlabels = (dataType?: string, dataTypesResponse?: DataTypesAPIResponse[]) => {
  // Find the configuration for the given dataType in the API response
  const dataTypeConfig = dataTypesResponse?.find((dt) => dt.name === dataType);

  // Initialize an object to store the UI labels
  const uiLabels: { [key: string]: string } = {};

  // For amount/number types, generate composite options if thousandsGrouping is available
  if (
    (dataType === 'amount' || dataType === 'number') &&
    dataTypeConfig?.options?.decimalSymbol &&
    dataTypeConfig?.options?.thousandsGrouping
  ) {
    // Only generate the 3 supported combinations:
    // 1. period decimal + standard grouping (3,3)
    // 2. comma decimal + standard grouping (3,3)
    // 3. period decimal + Indian grouping (2,3)
    const supportedCombinations = [
      { decimal: '.', grouping: '3, 3' },
      { decimal: ',', grouping: '3, 3' },
      { decimal: '.', grouping: '2, 3' },
    ];

    supportedCombinations.forEach(({ decimal, grouping }) => {
      const compositeKey = getCompositeFormatKey(decimal, grouping);
      if (FIELDS_UI_LABELS[compositeKey]) {
        uiLabels[compositeKey] = FIELDS_UI_LABELS[compositeKey];
      }
    });

    return uiLabels;
  }

  // For date type or amount/number without thousandsGrouping, use original behavior
  const options = dataTypeConfig?.options?.order || dataTypeConfig?.options?.decimalSymbol || [];

  // Populate the uiLabels object with labels from FIELDS_UI_LABELS or fallback to the option itself
  options.forEach((option: any) => {
    uiLabels[option] = FIELDS_UI_LABELS[option] || option;
  });

  // Return the populated uiLabels object
  return uiLabels;
};
